// Compact C1 / pilot session watch on /admin (ADR-014). Reads first-party
// login watermarks + ui.* audit rows — no third-party analytics.
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { Card, CardBody, CardHeader, EmptyState, Spinner } from "../../components/ui.js";
import { relativeAge } from "./health.js";

interface LoginRow {
  email: string;
  name: string;
  lastLoginAt: string | null;
  previousLoginAt: string | null;
}

interface AuditRow {
  at: string;
  actorEmail: string;
  action: string;
  entity: string;
  entityId: string;
}

export function SessionWatchPanel(): JSX.Element {
  const logins = useQuery({
    queryKey: qk.reportsLogins,
    queryFn: () => api.get<LoginRow[]>("/reports/logins"),
    refetchInterval: 60000,
  });
  const ui = useQuery({
    queryKey: qk.reportsAuditUi,
    queryFn: () => api.get<AuditRow[]>("/reports/audit?entity=ui"),
    refetchInterval: 60000,
  });

  return (
    <Card>
      <CardHeader>Session watch</CardHeader>
      <CardBody className="space-y-3 text-sm">
        <p className="text-xs text-slate-500">
          Login watermarks and in-app page events (no save required). C1 emails and SQL:
          pilot repo SESSION-WATCH.md.
        </p>
        {logins.isLoading || ui.isLoading ? (
          <Spinner label="Loading session watch…" />
        ) : logins.isError || ui.isError ? (
          <EmptyState>Could not load session watch.</EmptyState>
        ) : (
          <>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent logins</h3>
              {(logins.data ?? []).length === 0 ? (
                <p className="mt-1 text-xs text-slate-400">No last_login_at yet this lane.</p>
              ) : (
                <ul className="mt-1 space-y-1">
                  {(logins.data ?? []).slice(0, 12).map((u) => (
                    <li key={u.email} className="flex flex-wrap justify-between gap-2">
                      <span className="truncate">{u.name || u.email}</span>
                      <span className="text-xs text-slate-400">{relativeAge(u.lastLoginAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent UI events</h3>
              {(ui.data ?? []).length === 0 ? (
                <p className="mt-1 text-xs text-slate-400">No ui.* events yet.</p>
              ) : (
                <ul className="mt-1 space-y-1">
                  {(ui.data ?? []).slice(0, 12).map((r) => (
                    <li key={`${r.at}-${r.actorEmail}-${r.action}`} className="flex flex-wrap justify-between gap-2">
                      <span className="truncate">
                        {r.action} · {r.actorEmail}
                      </span>
                      <span className="text-xs text-slate-400">{relativeAge(r.at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}
